import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { commandRegistry } from '@/commands/registry';
import { useDocumentStore } from '@/store/documentStore';
import { useEditorStore } from '@/store/editorStore';
import type { CommandContext } from '@/commands/types';

export interface ContextMenuProps {
  x: number;
  y: number;
  nodeId: string;
  onClose: () => void;
}

interface MenuItem {
  labelKey: string;
  commandId: string;
}

interface MenuSeparator {
  separator: true;
}

type MenuEntry = MenuItem | MenuSeparator;

function isSeparator(entry: MenuEntry): entry is MenuSeparator {
  return 'separator' in entry;
}

const menuEntries: MenuEntry[] = [
  { labelKey: 'contextMenu.addChild', commandId: 'node.addChild' },
  { labelKey: 'contextMenu.addSibling', commandId: 'node.addSiblingBelow' },
  { labelKey: 'contextMenu.edit', commandId: 'node.editText' },
  { separator: true },
  { labelKey: 'contextMenu.copy', commandId: 'clipboard.copy' },
  { labelKey: 'contextMenu.duplicate', commandId: 'clipboard.duplicate' },
  { labelKey: 'contextMenu.cut', commandId: 'clipboard.cut' },
  { labelKey: 'contextMenu.paste', commandId: 'clipboard.paste' },
  { separator: true },
  { labelKey: 'contextMenu.delete', commandId: 'node.delete' },
  { labelKey: 'contextMenu.toggleCollapse', commandId: 'node.toggleCollapse' },
  { labelKey: 'contextMenu.resetAutoLayout', commandId: 'node.resetPosition' },
  { labelKey: 'contextMenu.resetSize', commandId: 'node.resetSize' },
  { labelKey: 'contextMenu.attachments', commandId: 'attachments.open' },
  { labelKey: 'contextMenu.aiAssist', commandId: 'ai.open' },
  { labelKey: 'contextMenu.comments', commandId: 'comments.open' },
];

function getContext(): CommandContext {
  return {
    doc: useDocumentStore.getState().mapDoc,
    editorState: useEditorStore.getState(),
  };
}

export function ContextMenu({ x, y, nodeId, onClose }: ContextMenuProps) {
  const { t } = useTranslation();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Ensure the target node is selected so commands operate on it
    useEditorStore.getState().selectNode(nodeId);
  }, [nodeId]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  function handleItemClick(commandId: string) {
    commandRegistry.execute(commandId, getContext());
    onClose();
  }

  return (
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        left: x,
        top: y,
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-default)',
        borderRadius: 6,
        padding: '4px 0',
        zIndex: 100,
        minWidth: 160,
        fontFamily: 'var(--font-family)',
        boxShadow: 'var(--shadow-md)',
      }}
    >
      {menuEntries.map((entry, index) => {
        if (isSeparator(entry)) {
          return (
            <div
              key={`sep-${index}`}
              style={{
                height: 1,
                background: 'var(--bg-surface-raised)',
                margin: '4px 8px',
              }}
            />
          );
        }
        return (
          <button
            key={entry.commandId}
            onClick={() => handleItemClick(entry.commandId)}
            style={{
              display: 'block',
              width: '100%',
              padding: '8px 16px',
              background: 'transparent',
              color: 'var(--text-primary)',
              border: 'none',
              textAlign: 'left',
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm)',
              fontFamily: 'inherit',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-surface-raised)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background =
                'transparent';
            }}
          >
            {t(entry.labelKey)}
          </button>
        );
      })}
    </div>
  );
}
